import React, { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { logoutUser } from "../Auth";

const DEFAULT_TIMEOUT_MINUTES = 30;

const getTimeoutMs = () => {
  const envValue = process.env.REACT_APP_IDLE_TIMEOUT_MINUTES;

  if (!envValue) {
    return DEFAULT_TIMEOUT_MINUTES * 60 * 1000;
  }

  const parsed = Number(envValue);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_TIMEOUT_MINUTES * 60 * 1000;
  }

  return parsed * 60 * 1000;
};

const IdleTimeoutHandler = ({ children }) => {
  const navigate = useNavigate();
  const timeoutIdRef = useRef(null);
  const hasLoggedOutRef = useRef(false);

  const clearExistingTimeout = () => {
    if (timeoutIdRef.current) {
      window.clearTimeout(timeoutIdRef.current);
      timeoutIdRef.current = null;
    }
  };

  const startTimeout = () => {
    clearExistingTimeout();

    const timeoutMs = getTimeoutMs();

    timeoutIdRef.current = window.setTimeout(() => {
      if (hasLoggedOutRef.current) return;

      hasLoggedOutRef.current = true;
      logoutUser();
      toast.error("You have been logged out due to inactivity.");
      navigate("/login", { replace: true });
    }, timeoutMs);
  };

  useEffect(() => {
    const handleActivity = () => {
      if (hasLoggedOutRef.current) return;
      startTimeout();
    };

    // Start the initial timer when component mounts
    startTimeout();

    const events = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    return () => {
      clearExistingTimeout();
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
    // We intentionally avoid adding startTimeout to deps to keep listeners stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <>{children}</>;
};

export default IdleTimeoutHandler;

